import { animate, style, transition, trigger } from '@angular/animations';
import { AfterViewInit, Component, ElementRef, input, OnDestroy, ViewChild, signal, output } from '@angular/core';
import { faMicrophone } from '@fortawesome/pro-light-svg-icons';
import { faPause, faPlay, faSpinnerScale } from '@fortawesome/pro-solid-svg-icons';

@Component({
  selector: 'empathy-audio-chat-card',
  templateUrl: './audio-chat-card.component.html',
  styleUrls: ['./audio-chat-card.component.scss'],
  animations: [
    trigger('rotateInOut', [
      transition(':enter', [style({ transform: 'rotate(-90deg)' }), animate('100ms', style({ transform: 'rotate(0deg)' }))]),
      transition(':leave', [style({ transform: 'rotate(-90deg)' }), animate('100ms', style({ transform: 'rotate(0deg)' }))])
    ])
  ]
})
export class AudioChatCardComponent implements AfterViewInit, OnDestroy {
  @ViewChild('audioPlayer') audioPlayer!: ElementRef;

  data = input.required<string>();
  waitingForAudio = output<boolean>();

  id: number = Date.now();

  faPause = faPause;
  faPlay = faPlay;
  faMicrophone = faMicrophone;
  faSpinnerScale = faSpinnerScale;

  buffered$ = signal(false);
  duration$ = signal('00:00');
  paused$ = signal(true);
  progressTime$ = signal('00:00');
  progress$ = signal(1);

  private audioListened = false;

  ngAfterViewInit(): void {
    this.initEventsServerAudio();
  }

  ngOnDestroy(): void {
    const audioElement = this.audioPlayer.nativeElement as HTMLAudioElement;
    audioElement.removeEventListener('timeupdate', this.timeUpdate);
    audioElement.removeEventListener('ended', this.endedEvent);
  }

  playPause(): void {
    const audio = this.audioPlayer.nativeElement as HTMLAudioElement;

    if (this.paused$()) {
      audio.currentTime = 0;
      audio.play();
    } else {
      audio.pause();
    }

    this.paused$.update((pause) => !pause);
  }

  private timeUpdate = (): void => {
    if (this.buffered$()) {
      const audioElement = this.audioPlayer.nativeElement as HTMLAudioElement;
      this.updateProgressTime(audioElement.currentTime);
      this.updateProgress(audioElement.currentTime, audioElement.duration);
    }
  };

  private endedEvent = (): void => {
    if (this.buffered$()) {
      this.paused$.set(true);
    }
  };

  private initEventsServerAudio(): void {
    const audioElement = this.audioPlayer.nativeElement as HTMLAudioElement;

    const playThroughEvent = (): void => {
      if (audioElement.duration === Infinity || Number.isNaN(audioElement.duration)) {
        audioElement.currentTime = 1e101;
      } else {
        this.setDuration(audioElement.duration);
        audioElement.currentTime = 0;
        this.buffered$.set(true);
        audioElement.removeEventListener('canplaythrough', playThroughEvent);
      }
    };

    audioElement.addEventListener('canplaythrough', playThroughEvent);
    audioElement.addEventListener('timeupdate', this.timeUpdate);
    audioElement.addEventListener('ended', this.endedEvent);

    audioElement.src = this.data();

    const audioContext = new AudioContext();

    fetch(this.data())
      .then((res) => res.blob())
      .then((myBlob) => myBlob.arrayBuffer())
      .then((buffer) => audioContext.decodeAudioData(buffer))
      .then(({ duration }) => {
        if (!Number.isNaN(duration)) {
          this.buffered$.set(true);
          this.setDuration(duration);
        }

        return audioContext.close();
      });

    audioElement.load();
  }

  private setDuration(time: number): void {
    const min = Math.floor((time / 60) << 0);
    const sec = Math.floor(time % 60);

    this.duration$.set(`${min}:${sec < 10 ? String(sec).padStart(2, '0') : sec}`);
  }

  private updateProgressTime(time: number): void {
    const min = Math.floor((time / 60) << 0);
    const sec = Math.floor(time % 60);

    this.progressTime$.set(`${min}:${sec < 10 ? String(sec).padStart(2, '0') : sec}`);
  }

  private updateProgress(time: number, duration: number): void {
    const watchedPercentage = (time / duration) * 100;

    if (watchedPercentage >= 90 && !this.audioListened) {
      this.audioListened = true;
      this.waitingForAudio.emit(false);
    }

    this.progress$.set(watchedPercentage);
  }
}
